import { z } from 'zod/v4';
import { CAPA_FINDING_STATUS } from '../../constants.js';
import {
  ActionAttachmentSchema,
  ActionPersonSchema,
  ActionResourceSchema,
} from './action-resource.js';

/**
 * The child-action rollup a plan carries instead of a status of its own.
 *
 * Every count is scoped to the reading company and excludes the actions that
 * company has archived, so two companies see different numbers for the same
 * plan. `calculatedProgress` is `done/total` as a percentage, and `null` rather
 * than `0` when the plan has no accessible children at all.
 */
export const PlanProgressSchema = z.object({
  toDo: z.number(),
  inProgress: z.number(),
  blocked: z.number(),
  inReview: z.number(),
  changesRequested: z.number(),
  done: z.number(),
  total: z.number(),
  calculatedProgress: z.number().nullable(),
});

export type PlanProgress = z.infer<typeof PlanProgressSchema>;

const capaFindingStatusValues = Object.values(CAPA_FINDING_STATUS) as [
  string,
  ...string[],
];

/**
 * One audit finding reached through the plan's child actions. Plans never link
 * a finding directly — this is the distinct set of `audit_finding_id`s carried
 * by the children the reading company can see.
 *
 * `status` is a live rollup (see `CAPA_FINDING_STATUS`), not the finding's own
 * column, and `isArchived` is the raw 0/1 for *this* company's archive shelf.
 */
export const PlanFindingSchema = z.object({
  id: z.string(),
  auditId: z.string().nullable(),
  companyId: z.string().optional(),
  title: z.string(),
  description: z.string().nullable(),
  urgency: z.string().nullable(),
  source: z.string().nullable(),
  impactAreaId: z.string().nullable(),
  type: z.string().nullable(),
  dueDate: z.union([z.string(), z.date()]).nullable(),
  isArchived: z.number(),
  status: z.enum(capaFindingStatusValues),
  assigneeUserId: z.string().nullable(),
  externalSource: z.string().nullable(),
  tags: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      colorCode: z.string(),
    }),
  ),
});

export type PlanFinding = z.infer<typeof PlanFindingSchema>;

/**
 * One corrective/preventive action **plan** (a `CAP` row of the same table the
 * actions live in).
 *
 * Shared with `ActionResourceSchema` field for field wherever the two reads
 * project the same column, and it reuses that schema's person, source, tag,
 * owner and company shapes rather than redeclaring them. What makes a plan
 * different:
 *
 * - `status`, `startedAt` and `endedAt` are **derived**, never the stored
 *   columns. Status is the child rollup; the dates come from the children too,
 *   and `endedAt` stays `null` until every accessible child is DONE. Filtering
 *   (`filter.status`) and sorting (`sort=status`) still hit the *stored* column,
 *   so a filtered list can contain rows whose returned status differs from the
 *   value that matched — the notify jobs rely on exactly that.
 * - `progress` and `findings` exist only here.
 * - `attachments` is a parsed, signed file array on the **detail** read and
 *   `null` on the list, which does not select the column — the same split the
 *   action reads use, so a caller can tell "no attachments" from "not asked
 *   for". `previewUrl` is a time-limited signed URL for a private-bucket file
 *   and the CDN URL otherwise.
 * - `cap`, `linkedActions`, `remediationRequest` and `generatedBy` are
 *   action-only and absent.
 */
export const PlanResourceSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  type: z.string().nullable(),
  impactAreaId: z.string().nullable(),
  sourceType: z.string().nullable(),
  sourceId: z.string().nullable(),
  priority: z.string().nullable(),
  status: z.string().nullable(),
  dueDate: z.date().nullable(),
  createdAt: z.date().nullable(),
  updatedAt: z.date().nullable(),
  auditor: z.string().nullable(),
  finding: z.string().nullable(),
  goals: z.string().nullable(),
  requirement: z.string().nullable(),
  managementResponse: z.string().nullable(),
  recommendation: z.string().nullable(),
  rootCause: z.string().nullable(),
  isArchived: z.number(),
  startedAt: z.date().nullable(),
  endedAt: z.date().nullable(),
  attachments: z.array(ActionAttachmentSchema).nullable(),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string().nullable(),
  createdByCompanyName: z.string().nullable(),
  assignees: z.array(ActionPersonSchema),
  reporters: z.array(ActionPersonSchema),
  watchers: z.array(ActionPersonSchema),
  ownerCompanyId: z.string().nullable(),
  ownerCompanyName: z.string().nullable(),
  // Viewer-scoped: what the reading company recorded about the owner company.
  // The list read selects them; the detail read does not.
  internalCompanyCode: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  owner: ActionResourceSchema.shape.owner,
  source: ActionResourceSchema.shape.source,
  tags: ActionResourceSchema.shape.tags,
  companies: ActionResourceSchema.shape.companies,
  progress: PlanProgressSchema,
  findings: z.array(PlanFindingSchema),
});

export type PlanResource = z.infer<typeof PlanResourceSchema>;
